import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import Navigation from "./Navigation";
import Footer from "./Footer";
import { useLanguage } from "../context/LanguageContext";

// Define image paths
const photography = [
  {
    id: 1,
    title: {
      en: "Royal Portuguese Reading Room Ceiling",
      zh: "巴西皇家葡萄牙阅览室天花板",
      ja: "ブラジル王立ポルトガル読書室の天井",
    },
    image: "/assets/personal/brazil_1_1.jpg",
    orientation: 1,
  },
  {
    id: 2,
    title: {
      en: "People Feeding Monkeys on Corcovado Mountain",
      zh: "基督山上的人们在喂猴子",
      ja: "コルコバードの丘で猿に餌をやる人々",
    },
    image: "/assets/personal/brazil_2_1.jpg",
    orientation: 1,
  },
  {
    id: 3,
    title: {
      en: "Maintenance Worker at Rio Cathedral",
      zh: "里约大教堂的维修工人",
      ja: "リオ大聖堂のメンテナンス作業員",
    },
    image: "/assets/personal/brazil_3_0.jpg",
    orientation: 0,
  },
  {
    id: 4,
    title: {
      en: "Iguazu Falls",
      zh: "伊瓜苏瀑布",
      ja: "イグアスの滝",
    },
    image: "/assets/personal/brazil_4_1.jpg",
    orientation: 1,
  },
  {
    id: 5,
    title: {
      en: "Beachcomber at Coney Island",
      zh: "康尼岛海边拾荒的人",
      ja: "コニーアイランドの浜辺の拾い集める人",
    },
    image: "/assets/personal/coney_island_1_1.jpg",
    orientation: 1,
  },
  {
    id: 6,
    title: {
      en: "Bird on Temple Roof",
      zh: "山西古庙屋顶上的小鸟",
      ja: "山西の古い寺院の屋根の上の鳥",
    },
    image: "/assets/personal/shanxi_1_1.jpg",
    orientation: 1,
  },
  {
    id: 7,
    title: {
      en: "Abandoned Train Cafe",
      zh: "废弃火车改造的咖啡厅",
      ja: "廃列車のカフェ",
    },
    image: "/assets/personal/Qinghai_1_1.jpg",
    orientation: 1,
  },
  {
    id: 8,
    title: {
      en: "Eagle on Zhuoer Mountain",
      zh: "卓尔山上的鹰",
      ja: "ゾオル山の鷲",
    },
    image: "/assets/personal/Qinghai_2_1.jpg",
    orientation: 1,
  },
  {
    id: 9,
    title: {
      en: "Abandoned Salt Lake Railway",
      zh: "盐湖废弃的轨道",
      ja: "塩湖の廃線",
    },
    image: "/assets/personal/Qinghai_3_0.jpg",
    orientation: 0,
  },
  {
    id: 10,
    title: {
      en: "Iceland Waterfall",
      zh: "冰岛瀑布",
      ja: "アイスランドの滝",
    },
    image: "/assets/personal/Iceland_1_1.jpg",
    orientation: 1,
  },
  {
    id: 11,
    title: {
      en: "Iceland Black Sand Beach",
      zh: "远处的冰岛黑沙滩",
      ja: "アイスランドのブラックサンドビーチ",
    },
    image: "/assets/personal/Iceland_2_0.jpg",
    orientation: 0,
  },
  {
    id: 12,
    title: {
      en: "Family at Sunset",
      zh: "夕阳下玩耍的一家人",
      ja: "夕日の下の家族",
    },
    image: "/assets/personal/Qinghai_4_1.jpg",
    orientation: 1,
  },
  {
    id: 13,
    title: {
      en: "Sunlight on Brazilian Beach",
      zh: "阳光照到巴西的沙滩上",
      ja: "ブラジルのビーチに差し込む太陽光",
    },
    image: "/assets/personal/brazil_5_1.jpg",
    orientation: 1,
  },
];

// PhotoItem component for better image handling
const PhotoItem = ({
  photo,
  selectedPhoto,
  setSelectedPhoto,
  loadedImages,
  handleImageLoad,
}) => {
  const { language } = useLanguage();

  return (
    <div
      className={`overflow-hidden group relative photo-item opacity-0 transition-all duration-700 
      ${selectedPhoto === photo.id ? "z-20 scale-100" : "hover:z-10"} 
      flex justify-center items-center`}
      onClick={() =>
        setSelectedPhoto(selectedPhoto === photo.id ? null : photo.id)
      }
    >
      <div
        className={`transition-all duration-500 transform ${
          selectedPhoto === photo.id ? "scale-110" : "group-hover:scale-105"
        } relative shadow-lg hover:shadow-xl rounded-md overflow-hidden`}
        style={{
          height: photo.orientation === 0 ? "480px" : "320px", // 固定高度
          width: photo.orientation === 0 ? "320px" : "480px", // 固定宽度
          maxWidth: "100%", // 确保在小屏幕上不溢出
        }}
      >
        <img
          src={photo.image}
          alt={photo.title[language]}
          className={`absolute top-0 left-0 w-full h-full object-cover transition-all duration-700 ${
            loadedImages.includes(photo.id) ? "opacity-100" : "opacity-0"
          }`}
          loading="lazy"
          onLoad={() => handleImageLoad(photo.id)}
        />

        {/* Interactive overlay with different states - Moved inside the relative container */}
        <div
          className={`absolute transition-all duration-500 flex items-center justify-center ${
            selectedPhoto === photo.id
              ? "bg-black bg-opacity-70"
              : "bg-black bg-opacity-0 group-hover:bg-opacity-40"
          }`}
          style={{
            top: "-6px",
            left: "-6px",
            right: "-6px",
            bottom: "-6px",
            width: "calc(100% + 12px)",
            height: "calc(100% + 12px)",
          }}
        >
          <div
            className={`text-center px-4 transition-all duration-500 ${
              selectedPhoto === photo.id
                ? "opacity-100 transform translate-y-0 scale-100"
                : "opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0"
            }`}
          >
            <h4 className="font-light text-white text-xl mb-2 text-shadow">
              {photo.title[language]}
            </h4>

            {/* Extra content only shown when selected */}
            <div
              className={`transition-all duration-500 delay-300 ${
                selectedPhoto === photo.id
                  ? "opacity-100 max-h-40"
                  : "opacity-0 max-h-0 overflow-hidden"
              }`}
            >
              <p className="text-gray-300 text-sm mb-4">
                {language === "en"
                  ? "Click image to view details"
                  : language === "zh"
                  ? "点击图片查看详情"
                  : "クリックして詳細を表示"}
              </p>
              <button className="bg-teal-600 text-white px-4 py-2 text-sm rounded-sm hover:bg-teal-700 transition-colors">
                {language === "en"
                  ? "View Full Size"
                  : language === "zh"
                  ? "查看原图"
                  : "全画面表示"}
              </button>
            </div>
          </div>
        </div>

        {/* Image corner indicators */}
        <div
          className={`absolute top-0 left-0 w-5 h-5 border-t-2 border-l-2 border-white transition-all duration-300 opacity-0 ${
            selectedPhoto === photo.id || loadedImages.includes(photo.id)
              ? "group-hover:opacity-100"
              : ""
          }`}
        ></div>
        <div
          className={`absolute top-0 right-0 w-5 h-5 border-t-2 border-r-2 border-white transition-all duration-300 opacity-0 ${
            selectedPhoto === photo.id || loadedImages.includes(photo.id)
              ? "group-hover:opacity-100"
              : ""
          }`}
        ></div>
        <div
          className={`absolute bottom-0 left-0 w-5 h-5 border-b-2 border-l-2 border-white transition-all duration-300 opacity-0 ${
            selectedPhoto === photo.id || loadedImages.includes(photo.id)
              ? "group-hover:opacity-100"
              : ""
          }`}
        ></div>
        <div
          className={`absolute bottom-0 right-0 w-5 h-5 border-b-2 border-r-2 border-white transition-all duration-300 opacity-0 ${
            selectedPhoto === photo.id || loadedImages.includes(photo.id)
              ? "group-hover:opacity-100"
              : ""
          }`}
        ></div>
      </div>
    </div>
  );
};

const Personal = () => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [loadedImages, setLoadedImages] = useState([]);
  const { language } = useLanguage();
  const galleryRef = useRef(null);

  // Add each loaded image to state
  const handleImageLoad = (id) => {
    if (!loadedImages.includes(id)) {
      setLoadedImages((prev) => [...prev, id]);
    }
  };

  // Reset selected photo when section changes
  useEffect(() => {
    return () => setSelectedPhoto(null);
  }, []);

  // Scroll animation effect
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("opacity-100");
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // 观察所有照片项目
    const photoItems = document.querySelectorAll(".photo-item");
    if (photoItems.length > 0) {
      photoItems.forEach((item, index) => {
        item.style.transitionDelay = `${index * 100}ms`;
        observer.observe(item);
      });
    }

    return () => {
      const photoItems = document.querySelectorAll(".photo-item");
      if (photoItems.length > 0) {
        photoItems.forEach((item) => {
          observer.unobserve(item);
        });
      }
    };
  }, []);

  // 自定义CSS样式
  const customStyles = `
    .text-shadow {
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
    }
  `;

  return (
    <div className="flex flex-col min-h-screen">
      <style>{customStyles}</style>
      <Navigation activeSection="personal" />

      <section className="min-h-screen pt-32 pb-16 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-light mb-16 text-center">
            {language === "en"
              ? "Personal"
              : language === "zh"
              ? "个人"
              : "個人"}
          </h2>

          <div className="mb-20">
            <h3 className="text-2xl font-light mb-8 text-center">
              {language === "en"
                ? "About Me"
                : language === "zh"
                ? "关于我"
                : "私について"}
            </h3>
            <div className="max-w-3xl mx-auto text-lg text-gray-600 leading-relaxed">
              <p className="mb-6">
                {language === "en"
                  ? "Beyond coding and academics, I'm passionate about photography and visual storytelling. I find that both programming and photography share common elements of composition, attention to detail, and creative problem-solving."
                  : language === "zh"
                  ? "除了编程和学术研究，我对摄影和视觉叙事充满热情。我发现编程和摄影都有共同的元素：构图、细节关注和创造性解决问题。"
                  : "プログラミングと学問以外に、私は写真撮影とビジュアルストーリーテリングに情熱を注いでいます。プログラミングと写真撮影には、構図、細部へのこだわり、創造的な問題解決という共通の要素があると感じています。"}
              </p>
              <p>
                {language === "en"
                  ? "When I'm not working on software projects or studying, you'll find me exploring urban landscapes with my camera, experimenting with new programming languages, or enjoying a good book on technology and innovation."
                  : language === "zh"
                  ? "当我不在编写软件项目或学习时，你会发现我在用相机探索城市景观，尝试新的编程语言，或者阅读关于技术和创新的好书。"
                  : "ソフトウェアプロジェクトや勉強をしていない時は、カメラを持って都市の風景を探索したり、新しいプログラミング言語を試したり、技術やイノベーションに関する良書を楽しんだりしています。"}
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-light mb-10 text-center">
              {language === "en"
                ? "Photography"
                : language === "zh"
                ? "摄影作品"
                : "写真作品"}
            </h3>

            {/* Interactive photo gallery with flex layout */}
            <div className="mx-auto mb-20">
              <Suspense
                fallback={
                  <div className="w-full text-center py-10">
                    {language === "en"
                      ? "Loading gallery..."
                      : language === "zh"
                      ? "加载图库中..."
                      : "ギャラリーを読み込み中..."}
                  </div>
                }
              >
                {/* 横向照片展示 */}
                <h4 className="text-xl font-light mb-8 text-center text-gray-700 border-b border-gray-200 pb-2 max-w-3xl mx-auto">
                  {language === "en"
                    ? "Landscape Photography"
                    : language === "zh"
                    ? "横向摄影作品"
                    : "横向の写真作品"}
                </h4>
                <div
                  ref={(el) => {
                    galleryRef.current = el;
                  }}
                  className="flex flex-wrap justify-center gap-8 relative mx-auto mb-16"
                >
                  {photography
                    .filter((photo) => photo.orientation === 1)
                    .map((photo) => (
                      <PhotoItem
                        key={photo.id}
                        photo={photo}
                        selectedPhoto={selectedPhoto}
                        setSelectedPhoto={setSelectedPhoto}
                        loadedImages={loadedImages}
                        handleImageLoad={handleImageLoad}
                      />
                    ))}
                </div>

                {/* 纵向照片展示 */}
                <h4 className="text-xl font-light mb-8 text-center text-gray-700 border-b border-gray-200 pb-2 max-w-3xl mx-auto">
                  {language === "en"
                    ? "Portrait Photography"
                    : language === "zh"
                    ? "纵向摄影作品"
                    : "縦向の写真作品"}
                </h4>
                <div className="flex flex-wrap justify-center gap-8 relative mx-auto">
                  {photography
                    .filter((photo) => photo.orientation === 0)
                    .map((photo) => (
                      <PhotoItem
                        key={photo.id}
                        photo={photo}
                        selectedPhoto={selectedPhoto}
                        setSelectedPhoto={setSelectedPhoto}
                        loadedImages={loadedImages}
                        handleImageLoad={handleImageLoad}
                      />
                    ))}
                </div>
              </Suspense>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Personal;
